<script setup lang="ts">
import type { SignOutVisitorLogForm } from '@/types/hvl/forms'
import { signOutVisitorLog } from '@/services/hvl.service'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import { reactive, ref } from 'vue'
import type { VisitorLog } from '@/types/hvl/VisitorLog'
import { DateTime } from 'luxon'

const props = defineProps<{
  visitorLog: VisitorLog
}>()

const model = defineModel<boolean>()

const signOutVisitorLogFormRef = ref<FormInstance>()
const signOutVisitorLogForm = reactive<SignOutVisitorLogForm>({
  sign_out: DateTime.now().toISO(),
})

const rules = reactive<FormRules<SignOutVisitorLogForm>>({
  sign_out: [
    {
      validator: (rule, value, callback) => {
        if (!value) {
          return callback(new Error('Sign Out time is required.'))
        }
        if (DateTime.fromISO(props.visitorLog.sign_in!) >= DateTime.fromISO(value)) {
          return callback(new Error('Sign Out time must be after Sign In time.'))
        }
        callback()
      },
      trigger: 'blur',
    },
  ],
})

const queryClient = useQueryClient()

const signOut = useMutation({
  mutationFn: ({ id, form }: { id: number; form: SignOutVisitorLogForm }) => signOutVisitorLog(id, form),
  onSuccess: () => queryClient.resetQueries({ queryKey: ['visitor-logs'] }),
})

/**
 * Handles form submit.
 *
 * @param formEl form element ref.
 */
const submitForm = async (formEl: FormInstance | undefined) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  formEl?.validate(async (valid, fields) => {
    if (valid) {
      try {
        await signOut.mutateAsync({ id: props.visitorLog!.id, form: signOutVisitorLogForm })
        ElMessage({
          type: 'success',
          message: 'Signed out log',
        })
        resetFormCloseDialog()
      } catch (err: any) {
        ElMessage({
          type: 'error',
          message: `Operation failed: ${err.message}`,
        })
      }
    }
  })
}

const initForm = async () => {
  // Populate the sign out value with the current time
  signOutVisitorLogForm.sign_out = DateTime.now().toISO()
}

/**
 * Resets form, closes dialog.
 */
const resetFormCloseDialog = () => {
  resetForm(signOutVisitorLogFormRef.value)
  model.value = false
}

/**
 * Resets the form.
 *
 * @param formEl form element ref.
 */
const resetForm = (formEl: FormInstance | undefined) => {
  formEl?.resetFields()
}
</script>

<template>
  <el-dialog v-model="model" title="Sign Out Log" :show-close="false" :width="320" @open="initForm">
    <el-form
      ref="signOutVisitorLogFormRef"
      :model="signOutVisitorLogForm"
      :rules="rules"
      label-width="auto"
      label-position="left"
      hide-required-asterisk
    >
      <el-form-item label="Sign In">
        <el-input
          :placeholder="DateTime.fromISO($props.visitorLog.sign_in!).toFormat('yyyy-MM-dd HH:mm:ss')"
          disabled
          readonly
        />
      </el-form-item>
      <el-form-item label="Sign Out" prop="sign_out">
        <el-date-picker
          v-model="signOutVisitorLogForm.sign_out"
          type="datetime"
          value-format="YYYY-MM-DDTHH:mm:ss.SSSZ"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="resetFormCloseDialog">Cancel</el-button>
      <el-button type="primary" @click="submitForm(signOutVisitorLogFormRef)">Submit</el-button>
    </template>
  </el-dialog>
</template>
